import isEmpty from 'lodash.isempty'

import { isFunction, isNumber } from '@core/helpers'
import { CancelToken } from '@http/api'
import {
  getSourcingMerchantInfos,
  getSourcingOrders,
  getSourcingPending,
  getSourcingReceived,
} from '@http/endpoints'

const fetchOrders = async (
  { commit, dispatch, getters },
  request,
  queryParams,
  loadNextPage,
) => {
  if (getters.hasCancelToken) {
    getters.cancelToken()
  }

  const cancelToken = CancelToken.source()
  commit('setCancelToken', cancelToken)
  commit('setLoading', true)

  const { payload } = await dispatch(
    'http/request',
    {
      request,
      queryParams: {
        ...queryParams,
        ...(loadNextPage &&
          isNumber(getters.nextPageNumber) && { page: getters.nextPageNumber }),
      },
      cancelToken,
    },
    { root: true },
  )

  commit('setFilters', queryParams)
  if (!isEmpty(payload)) {
    commit('setNextPageNumber', payload)
    commit('setCount', payload)
    if (loadNextPage) {
      commit('setNextPageOrders', payload)
    } else {
      commit('setOrders', payload)
    }
  }
  commit('setLoading', false)
  commit('setCancelToken', {})
}

export default {
  namespaced: true,

  state: () => ({
    filters: {},
    hasFilters: false,
    hasOrders: false,
    loading: false,
    nextPageNumber: null,
    orders: [],
    count: 0,
    markets: [],
    token: {
      cancelToken: {},
    },
  }),

  getters: {
    cancelToken: (state) => () => state.token.cancelToken.cancel(),
    filters: (state) => state.filters,
    hasCancelToken: (state) => isFunction(state.token.cancelToken.cancel),
    hasFilters: (state) => !isEmpty(state.filters),
    hasOrders: (state) => !isEmpty(state.orders),
    loading: (state) => state.loading,
    nextPageNumber: (state) => state.nextPageNumber,
    orders: (state) => state.orders,
    count: (state) => state.count,
    markets: (state) => state.markets,
  },

  mutations: {
    setCancelToken(state, cancelToken) {
      state.token = { cancelToken }
    },

    setFilters(state, filters) {
      state.filters = filters
    },

    setLoading(state, loading) {
      state.loading = loading
    },

    setNextPageNumber(state, { next }) {
      state.nextPageNumber = isEmpty(next)
        ? null
        : parseInt(next.match(/page=(\d+)/)[1], 10)
    },

    setOrders(state, { results }) {
      state.orders = results
    },

    setCount(state, { count }) {
      state.count = count
    },

    setMarkets(state, { markets }) {
      state.markets = markets
    },

    setNextPageOrders(state, { results }) {
      state.orders = [...state.orders, ...results]
    },

    resetStore(state) {
      state.filters = {}
      state.hasFilters = false
      state.hasOrders = false
      state.loading = false
      state.nextPageNumber = null
      state.orders = []
      state.count = 0
    },
  },

  actions: {
    reset({ commit }) {
      commit('resetStore')
    },

    async fetchSourcingOrders(store, { queryParams, loadNextPage }) {
      if (isEmpty(store.getters.markets)) {
        store.dispatch('fetchSourcingMerchantInfos')
      }

      await fetchOrders(store, getSourcingOrders, queryParams, loadNextPage)
    },

    async fetchSourcingReceivedOrders(store, { queryParams, loadNextPage }) {
      await fetchOrders(store, getSourcingReceived, queryParams, loadNextPage)
    },

    async fetchSourcingPendingOrders(store, { queryParams, loadNextPage }) {
      await fetchOrders(store, getSourcingPending, queryParams, loadNextPage)
    },

    async fetchSourcingMerchantInfos({ dispatch, commit }) {
      const { payload } = await dispatch(
        'http/request',
        {
          request: getSourcingMerchantInfos,
        },
        { root: true },
      )

      if (!isEmpty(payload)) {
        commit('setMarkets', payload)
      }
    },
  },
}
